import { Routes, Route, Outlet } from "react-router";
import { Suspense, useEffect, lazy } from "react";
import { Toaster } from "sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import { useLocation } from "react-router";
import TagManager from "react-gtm-module";
import "@copilotkit/react-ui/styles.css";
import "react-toastify/dist/ReactToastify.css";

// Providers
import AuthProvider from "@/providers/AuthProvider";
import OrganizationProvider from "@/providers/OrganizationProvider";
import ConfigProvider from "@/providers/ConfigProvider";
import ThemeProvider from "@/providers/ThemeProvider";
import CopilotKitProvider from "@/providers/CopilotKitProvider";

// Layouts
import MainLayout from "./layouts/MainLayout";
import AuthLayout from "./layouts/AuthLayout";

// Pages
import Login from "./pages/auth/Login";
import CloverAppSelection from "./pages/auth/clover/app-selection/page";
import AuthError from "./pages/auth/error/page";
import Dashboard from "./pages/dashboard/Dashboard";
import CustomerDetailsPage from "@/pages/customers/[id]/page";
import SubscriptionPage from "./pages/subscription/page";
import BillingPage from "./pages/billing/page";

// Lazy-loaded pages
const Transactions = lazy(() => import("./pages/transactions/page"));
const Customers = lazy(() => import("./pages/customers/page"));
const AutomatedCampaigns = lazy(
  () => import("./pages/campaigns/automated/page")
);
const Products = lazy(() => import("./pages/products/page"));
const CampaignsLayout = lazy(() => import("./pages/campaigns/layout"));
const CampaignsSms = lazy(() => import("./pages/campaigns/sms/page"));
const CampaignsBroadcast = lazy(
  () => import("./pages/campaigns/broadcast/page")
);
const CampaignsReport = lazy(() => import("./pages/campaigns/report/page"));
const CampaignSmsDetails = lazy(
  () => import("./pages/campaigns/sms/details/page")
);
const ReportsCustomers = lazy(() => import("./pages/reports/customers/page"));
const ReportsCustomersOverview = lazy(
  () => import("./pages/reports/customers/overview/page")
);
const ReportsCustomersBehavior = lazy(
  () => import("./pages/reports/customers/behavior/page")
);
const ReportsCustomersPurchasePattern = lazy(
  () => import("./pages/reports/customers/purchasePattern/page")
);
const ReportsSales = lazy(() => import("./pages/reports/sales/page"));
const SalesOverview = lazy(() => import("./pages/reports/sales/overview/Page"));
const SalesTrends = lazy(() => import("./pages/reports/sales/trends/Page"));
const SalesProducts = lazy(() => import("./pages/reports/sales/products/Page"));
const UserProfile = lazy(() => import("./pages/user-profile/page"));
const NotFound = lazy(() => import("./pages/not-found"));
const AdminSwitch = lazy(() => import("./pages/admin/switch/page"));

// Create a query client
const queryClient = new QueryClient();

// Wrapper component for campaigns to use Outlet
const CampaignsOutlet = () => {
  return (
    <CampaignsLayout>
      <Outlet />
    </CampaignsLayout>
  );
};

function App() {
  const location = useLocation();
  const isBasicView =
    location.pathname === "/login" ||
    location.pathname === "/admin/switch" ||
    location.pathname.startsWith("/auth/clover/app-selection") ||
    location.pathname === "/auth/error";

  // Initialize Google Tag Manager
  useEffect(() => {
    if (import.meta.env.VITE_GTM_ID) {
      TagManager.initialize({ gtmId: import.meta.env.VITE_GTM_ID });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <div className="font-sans">
          <Toaster position="top-right" />
          <Suspense fallback={<div>Loading...</div>}>
            {isBasicView ? (
              <Routes>
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/admin/switch" element={<AdminSwitch />} />
                  <Route
                    path="/auth/clover/app-selection"
                    element={<CloverAppSelection />}
                  />
                  <Route path="/auth/error" element={<AuthError />} />
                </Route>
              </Routes>
            ) : (
              <ConfigProvider>
                <OrganizationProvider>
                  <ThemeProvider>
                    <CopilotKitProvider>
                      <Routes>
                        <Route element={<MainLayout />}>
                          <Route path="/" element={<Dashboard />} />
                          <Route
                            path="/transactions"
                            element={<Transactions />}
                          />
                          <Route path="/customers" element={<Customers />} />
                          <Route
                            path="/automated-campaigns"
                            element={<AutomatedCampaigns />}
                          />
                          <Route path="/products" element={<Products />} />

                          {/* Campaigns section with nested routes */}
                          <Route
                            path="/campaigns"
                            element={<CampaignsOutlet />}
                          >
                            <Route path="sms" element={<CampaignsSms />} />
                            <Route
                              path="broadcast"
                              element={<CampaignsBroadcast />}
                            />
                            <Route
                              path="report"
                              element={<CampaignsReport />}
                            />
                            <Route
                              path="sms/:id"
                              element={<CampaignSmsDetails />}
                            />
                            <Route index element={<CampaignsSms />} />
                          </Route>

                          {/* Reports section */}
                          <Route
                            path="/reports/customers"
                            element={<ReportsCustomers />}
                          />
                          <Route
                            path="/reports/customers/overview"
                            element={<ReportsCustomersOverview />}
                          />
                          <Route
                            path="/reports/customers/behavior"
                            element={<ReportsCustomersBehavior />}
                          />
                          <Route
                            path="/reports/customers/purchase-pattern"
                            element={<ReportsCustomersPurchasePattern />}
                          />
                          <Route
                            path="/reports/sales"
                            element={<ReportsSales />}
                          />
                          <Route
                            path="/reports/sales/overview"
                            element={<SalesOverview />}
                          />
                          <Route
                            path="/reports/sales/trends"
                            element={<SalesTrends />}
                          />
                          <Route
                            path="/reports/sales/products"
                            element={<SalesProducts />}
                          />

                          {/* User related routes */}
                          <Route
                            path="/user-profile"
                            element={<UserProfile />}
                          />
                          {/* Customer details route */}
                          <Route
                            path="/customers/:id"
                            element={<CustomerDetailsPage />}
                          />
                          <Route path="/billing" element={<BillingPage />} />
                          <Route
                            path="/subscription"
                            element={<SubscriptionPage />}
                          />
                          {/* 404 page */}
                          <Route path="*" element={<NotFound />} />
                        </Route>
                      </Routes>
                    </CopilotKitProvider>
                  </ThemeProvider>
                </OrganizationProvider>
              </ConfigProvider>
            )}
          </Suspense>
          <ToastContainer />
        </div>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
